/**
 * Gemeinsame Grundtypen für den Nidus-Integrationsvertrag und die Autopilot-API.
 * Quelle: docs/autopilot/02, 03, 04, 08. Diese Datei ist providerneutral und
 * enthält keine Laufzeitabhängigkeit außer zod (auch im Browser nutzbar).
 */
import { z } from 'zod';
export const Uuid = z.uuid();
export const IsoDateTime = z.iso.datetime({ offset: true });
export const Score = z.number().int().min(0).max(100);
/** Aktive Nidus-Status. GELOEST und GESCHLOSSEN sind nie auswählbar (02 „Auswahl“). */
export const AKTIVE_STATUS = [
    'NEU',
    'KLASSIFIZIERT',
    'IN_BEARBEITUNG',
    'WARTET_EXTERN',
    'WARTET_MELDER',
];
export const AktiverStatus = z.enum(AKTIVE_STATUS);
/** Vollständiger Nidus-Status (für Snapshots nach der Auswahl, z. B. „inzwischen gelöst“). */
export const VorgangStatus = z.enum([...AKTIVE_STATUS, 'GELOEST', 'GESCHLOSSEN']);
export const Trigger = z.enum(['MANUAL', 'SCHEDULED']);
export const ActorType = z.enum(['USER', 'SYSTEM']);
export const AuthorizationType = z.enum(['MANUAL_APPROVAL', 'COMPANY_POLICY']);
export const Assignment = z.enum(['ALL', 'UNASSIGNED']);
/** Mindestalter für SCHEDULED in Minuten; im MVP fest und nicht absenkbar (08 §1). */
export const MIN_TICKET_AGE_MINUTES = 30;
export const DEFAULT_INTERVAL_MINUTES = 60;
export const INTERVAL_MIN = 15;
export const INTERVAL_MAX = 1440;
export const INTERVAL_STEP = 15;
export const DEFAULT_MIN_CORRECTNESS = 90;
export const DEFAULT_MAX_ERROR_RISK = 10;
/**
 * Stabile Fehlercodes (04 „Fehlercodes“). Ergänzt um wenige technische Codes,
 * die der Vertrag für Validierung/Versionierung braucht.
 */
export const ERROR_CODES = [
    'AUTOMATION_DISABLED',
    'POLICY_CHANGED',
    'POLICY_NOT_SYNCED',
    'TICKET_TOO_YOUNG',
    'MANUAL_CLAIM_ACTIVE',
    'AUTOMATION_HOLD',
    'EXECUTION_DISABLED',
    'INSTANCE_MISMATCH',
    'SELECTION_EXPIRED',
    'TICKET_CHANGED',
    'SOURCE_CHANGED',
    'FOCUS_RESERVED',
    'ALREADY_RESOLVED',
    'OPERATION_ACTIVE',
    'PROFILE_NOT_ALLOWED',
    'EXCLUSION_MATCH',
    'CONTEXT_INCOMPLETE',
    'CONTEXT_TOO_LARGE',
    'NO_RECIPIENT',
    'DELIVERY_SUPPRESSED',
    'DELIVERY_FAILED',
    'DELIVERY_UNKNOWN',
    'SENT_NOT_RESOLVED',
    'IDEMPOTENCY_MISMATCH',
    'RATE_LIMITED',
    // technische Ergänzungen
    'VERSION_CONFLICT',
    'VALIDATION_FAILED',
    'NOT_FOUND',
    'UNAUTHENTICATED',
    'FORBIDDEN',
    'DEPENDENCY_UNAVAILABLE',
    'INTERNAL',
];
export const ErrorCode = z.enum(ERROR_CODES);
/** RFC 9457 Problem Details mit stabilem Code (04 „Sicherheit und Konventionen“). */
export const Problem = z.object({
    type: z.string().default('about:blank'),
    title: z.string(),
    status: z.number().int(),
    code: ErrorCode,
    correlationId: z.string(),
    retryable: z.boolean(),
    operationId: Uuid.optional(),
    instanceId: z.string().optional(),
    detail: z.string().optional(),
    /** Feldbezogene Fehler (z. B. 422 bei Aktivierung mit fehlenden Pflichtwerten, 08 §7). */
    fieldErrors: z.record(z.string(), z.string()).optional(),
});
export const PAGE_DEFAULT = 100;
export const PAGE_MAX = 500;
export const PageQuery = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(PAGE_MAX).default(PAGE_DEFAULT),
});
export function Page(item) {
    return z.object({
        items: z.array(item),
        nextCursor: z.string().nullable(),
    });
}
/** Tokenverbrauch; null = vom Provider nicht gemeldet (nie als 0 darstellen, 05 „Tokenrechnung“). */
export const UsageTotals = z.object({
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    totalTokens: z.number().int().nonnegative(),
    /** false, wenn mindestens ein Aufruf keine Usage gemeldet hat → „mindestens X, unvollständig“. */
    complete: z.boolean(),
    unknownCalls: z.number().int().nonnegative(),
});
//# sourceMappingURL=common.js.map